import { useEffect, useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { DateValidationError, DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";

import Modal from "./Modal";

export interface DateRangeModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional descriptive text shown below the title, above a divider */
  description?: string;
  onConfirm: (startDate: Dayjs, endDate: Dayjs | null) => void;
  primaryLabel?: string;
  secondaryLabel?: string;
  /** Heading shown above the date pickers */
  sectionTitle?: string;
  startDateLabel?: string;
  /** When false, only the start date picker is rendered */
  showEndDate?: boolean;
  endDateLabel?: string;
  /** Disables both buttons and shows a spinner while an onConfirm request is in flight */
  isSubmitting?: boolean;
  /** Shown inline when the last onConfirm attempt failed; the modal stays open so the user can retry */
  errorMessage?: string | null;
  /** Pre-populated value for the start date picker when the modal opens (and after it resets) */
  defaultStartDate?: Dayjs | null;
  /** Pre-populated value for the end date picker when the modal opens (and after it resets) */
  defaultEndDate?: Dayjs | null;
  /** Latest selectable end date; when omitted the end date picker has no upper bound */
  maxEndDate?: Dayjs | null;
  /** Validation message shown when the end date is later than maxEndDate */
  endDateMaxDateMessage?: string;
  /** When set, constrains the gap between start and end date to at most this many months */
  maxRangeMonths?: number;
  /** Validation message shown when the start/end gap exceeds maxRangeMonths */
  maxRangeMessage?: string;
}

export default function DateRangeModal({
  open,
  onClose,
  title,
  description,
  onConfirm,
  primaryLabel = "Confirm",
  secondaryLabel = "Cancel",
  sectionTitle = "Select a date range",
  startDateLabel = "Start date",
  showEndDate = true,
  endDateLabel = "End date",
  isSubmitting = false,
  errorMessage = null,
  defaultStartDate = null,
  defaultEndDate = null,
  maxEndDate = null,
  endDateMaxDateMessage = "End date should not be after the latest selectable date",
  maxRangeMonths,
  maxRangeMessage = maxRangeMonths ? `Date range cannot exceed ${maxRangeMonths} months` : "",
}: DateRangeModalProps) {
  const [startDate, setStartDate] = useState<Dayjs | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Dayjs | null>(defaultEndDate);
  const [startDateError, setStartDateError] = useState<DateValidationError | null>(null);
  const [endDateError, setEndDateError] = useState<DateValidationError | null>(null);

  // Reset only once the modal fully closes, not on every confirm attempt - so a failed
  // attempt's error can be shown alongside the dates the user already picked, for retrying.
  useEffect(() => {
    if (!open) {
      setStartDate(defaultStartDate);
      setEndDate(defaultEndDate);
      setStartDateError(null);
      setEndDateError(null);
    }
  }, [open, defaultStartDate, defaultEndDate]);

  // startDate/endDate stay non-null while a picker's text is merely invalid (e.g. out-of-range
  // or unparseable), so validity must also be tracked via onError, not just null-checks.
  const isConfirmDisabled =
    !startDate || !!startDateError || (showEndDate && (!endDate || !!endDateError)) || isSubmitting;

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleConfirm = () => {
    if (startDate && !isSubmitting) {
      onConfirm(startDate, showEndDate ? endDate : null);
    }
  };

  const getDateErrorMessage = (error: DateValidationError | null, constraintMessage: string): string => {
    if (error === "invalidDate") {
      return "Invalid date";
    }
    if (error === "minDate" || error === "maxDate") {
      return constraintMessage;
    }
    return "";
  };

  // Start date has no independent minDate constraint today, so a minDate error only ever
  // means the range-length limit was breached (endDate too far ahead of startDate).
  const startDateMinDate = maxRangeMonths && endDate ? endDate.subtract(maxRangeMonths, "month") : undefined;

  const getStartDateErrorMessage = (error: DateValidationError | null): string => {
    if (error === "minDate") {
      return getDateErrorMessage(error, maxRangeMessage);
    }
    return getDateErrorMessage(error, "Start date should not be after End date");
  };

  // End date's maxDate is the tighter of maxEndDate and the range-length limit, so a maxDate
  // error must be re-checked against endDate to know which constraint actually triggered it.
  const rangeLimitEndDate = maxRangeMonths && startDate ? startDate.add(maxRangeMonths, "month") : undefined;
  const endDateMaxDate =
    maxEndDate && rangeLimitEndDate
      ? maxEndDate.isBefore(rangeLimitEndDate)
        ? maxEndDate
        : rangeLimitEndDate
      : maxEndDate ?? rangeLimitEndDate ?? undefined;

  const getEndDateErrorMessage = (error: DateValidationError | null): string => {
    if (error === "maxDate" && endDate && rangeLimitEndDate && endDate.isAfter(rangeLimitEndDate)) {
      return getDateErrorMessage(error, maxRangeMessage);
    }
    return getDateErrorMessage(
      error,
      error === "maxDate" ? endDateMaxDateMessage : "End date should not be before Start date"
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={title}
      primaryLabel={primaryLabel}
      onPrimary={handleConfirm}
      primaryDisabled={isConfirmDisabled}
      secondaryLabel={secondaryLabel}
      onSecondary={handleClose}
      maxWidth="sm"
    >
      <Divider sx={{ mb: 3 }} />

      <Box pt={0}>
        {description && (
          <>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: "block" }}>
              {description}
            </Typography>
          </>
        )}
        <Box pt={2}></Box>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          {sectionTitle}
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DesktopDatePicker
                label={startDateLabel}
                format="DD/MM/YYYY"
                value={startDate}
                minDate={showEndDate ? startDateMinDate : undefined}
                maxDate={showEndDate && endDate ? endDate : undefined}
                onChange={(date) => setStartDate(date)}
                onError={(error) => setStartDateError(error)}
                disabled={isSubmitting}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!startDateError,
                    helperText: getStartDateErrorMessage(startDateError),
                  },
                }}
              />
            </Grid>
            {showEndDate && (
              <Grid item xs={12}>
                <DesktopDatePicker
                  label={endDateLabel}
                  format="DD/MM/YYYY"
                  value={endDate}
                  minDate={startDate ?? undefined}
                  maxDate={endDateMaxDate}
                  onChange={(date) => setEndDate(date)}
                  onError={(error) => setEndDateError(error)}
                  disabled={isSubmitting}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!endDateError,
                      helperText: getEndDateErrorMessage(endDateError),
                    },
                  }}
                />
              </Grid>
            )}
            {isSubmitting && (
              <Grid item xs={12} display="flex" alignItems="center" gap={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Generating...</Typography>
              </Grid>
            )}
            {!isSubmitting && errorMessage && (
              <Grid item xs={12}>
                <Typography variant="body2" color="error">
                  {errorMessage}
                </Typography>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </Box>
    </Modal>
  );
}
